import { html, render, nothing, svg, type TemplateResult } from 'lit-html';
import waterfallStyles from './pk-report-waterfall.css?inline';
import sharedTokens from '../pk-report-shared/pk-report-tokens.css?inline';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One span in the waterfall. Any time-ordered hierarchical event can be
 * represented — Claude Code tool calls, KB ticket state transitions, DevOps
 * build steps, etc.
 *
 * Color and label are always caller-provided; the component has no built-in
 * opinion about what categories mean.
 */
export interface PkWaterfallSpan {
    /** Unique span ID within this trace. */
    id:        string;
    /** Parent span ID — undefined / absent for root spans. */
    parentId?: string;
    /** Display label shown in the row and tooltip. */
    label:     string;
    /** ISO 8601 instant string — span start. */
    start:     string;
    /** ISO 8601 instant string — span end. */
    end:       string;
    /** Caller-defined category token (e.g. "session", "tool", "phase"). */
    category?: string;
    /** CSS color string for the span bar. */
    color:     string;
    /**
     * Indent depth (0 = root). Used to compute left offset; callers are
     * responsible for setting this correctly relative to parentId.
     */
    depth:     number;
    /**
     * Arbitrary key-value metadata shown in the tooltip.
     * Set `meta.isCriticalPath = true` to enable the critical-path highlight.
     */
    meta?:     Record<string, unknown>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Layout constants
// ─────────────────────────────────────────────────────────────────────────────

const ROW_HEIGHT    = 28;   // px per span row in the SVG coordinate space
const LABEL_WIDTH   = 200;  // px reserved for span labels on the left
const INDENT_PX     = 16;   // px per depth level
const MINIMAP_H     = 32;   // px — minimap height (SVG coordinate space)
const RULER_H       = 24;   // px — time ruler height
const TOOLTIP_OFFSET_X = 12;
const TOOLTIP_OFFSET_Y = 8;
const SVG_TOTAL_W   = 1000; // viewBox width; scales via CSS

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function parseMs(iso: string): number {
    try { return new Date(iso).getTime(); } catch { return 0; }
}

/** Format a UTC instant as HH:mm:ss.SSS */
function fmtTime(iso: string): string {
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        const pad = (n: number, w = 2) => String(n).padStart(w, '0');
        return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}.${String(d.getUTCMilliseconds()).padStart(3, '0')} UTC`;
    } catch { return iso; }
}

/** Auto-format a duration in milliseconds to a human string. */
function fmtDuration(ms: number): string {
    if (ms < 0) ms = 0;
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
    const m = Math.floor(ms / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

/**
 * Clamp a number to [min, max].
 */
function clamp(v: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, v));
}

// ─────────────────────────────────────────────────────────────────────────────
// <pk-report-waterfall>
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `<pk-report-waterfall>` — trace / session swimlane. v0.7
 *
 * Renders a set of time-ordered hierarchical spans as horizontal bars in a
 * vertical swimlane. Each span is one row; bar width = duration proportion;
 * depth-indented left edge. Primary consumer: Claude Code session view in the
 * AI Usage pack (Reports-Packs-Spec.md § 6.1 by-user view).
 *
 * **Data model is generic** — color, label, category are all caller-provided.
 * KB ticket lifecycle and DevOps PR lifecycle are phase-2 reuses.
 *
 * Set data imperatively via the `spans` property:
 * ```js
 * wf.spans = [ { id, label, start, end, color, depth, ... }, ... ];
 * ```
 *
 * Attributes:
 *   `trace-start` — ISO instant of the overall trace start (x-axis left).
 *   `trace-end`   — ISO instant of the overall trace end (x-axis right).
 *   `height`      — `auto` (default, grows with rows) or a px value.
 *   `title`       — optional panel heading.
 *   `minimap`     — boolean attribute; enables the 32 px minimap strip.
 *
 * Events:
 *   `pk-span-select` — `CustomEvent<{ span: PkWaterfallSpan }>` — fired on
 *                      row click or Enter key.
 *   `pk-range-zoom`  — `CustomEvent<{ from: string, to: string }>` — fired
 *                      when the user drag-selects a range on the minimap.
 *
 * Keyboard:
 *   ArrowUp / ArrowDown — move focus between span rows.
 *   Enter               — fire `pk-span-select` for focused row.
 *   Tab                 — cycles through minimap (if present) and first span.
 *
 * ARIA:
 *   `role="tree"` on the span container; each row has `role="treeitem"`,
 *   `aria-level={depth+1}`, and `aria-label` including label + duration.
 */
export class PkReportWaterfall extends HTMLElement {
    static readonly tagName = 'pk-report-waterfall';

    static get observedAttributes(): string[] {
        return ['trace-start', 'trace-end', 'height', 'title', 'minimap'];
    }

    private readonly _root: ShadowRoot;

    // Data
    private _spans: PkWaterfallSpan[] = [];

    // View range (for zoom). null means "show all".
    private _viewFrom: number | null = null;
    private _viewTo:   number | null = null;

    // Focused row index (into visible spans array)
    private _focusedIdx = -1;

    // Tooltip state
    private _tooltipSpan: PkWaterfallSpan | null = null;
    private _tooltipX = 0;
    private _tooltipY = 0;

    // Minimap drag state
    private _minimapDragging  = false;
    private _minimapDragStart = -1;
    private _minimapDragCur   = -1;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    connectedCallback(): void {
        this._render();
    }

    attributeChangedCallback(): void {
        if (this.isConnected) this._render();
    }

    // ── Public API ────────────────────────────────────────────────────────────

    /** Set span data programmatically. Resets zoom and focus. */
    set spans(value: PkWaterfallSpan[]) {
        this._spans     = Array.isArray(value) ? value : [];
        this._viewFrom  = null;
        this._viewTo    = null;
        this._focusedIdx = -1;
        this._tooltipSpan = null;
        this._render();
    }

    get spans(): PkWaterfallSpan[] {
        return this._spans;
    }

    // ── Attribute helpers ─────────────────────────────────────────────────────

    private _traceStartMs(): number {
        const v = this.getAttribute('trace-start');
        return v ? parseMs(v) : (this._spans.length ? Math.min(...this._spans.map(s => parseMs(s.start))) : 0);
    }

    private _traceEndMs(): number {
        const v = this.getAttribute('trace-end');
        return v ? parseMs(v) : (this._spans.length ? Math.max(...this._spans.map(s => parseMs(s.end))) : 0);
    }

    private _showMinimap(): boolean {
        return this.hasAttribute('minimap');
    }

    // ── View range ─────────────────────────────────────────────────────────────

    /** Effective view start/end in ms. Falls back to full trace range. */
    private _viewRange(): { from: number; to: number } {
        const traceStart = this._traceStartMs();
        const traceEnd   = this._traceEndMs();
        return {
            from: this._viewFrom ?? traceStart,
            to:   this._viewTo   ?? traceEnd,
        };
    }

    // ── Layout math ───────────────────────────────────────────────────────────

    /**
     * Convert a span's start/end (ms) to x positions within SVG_TOTAL_W,
     * given the current view range.
     */
    private _spanToSvg(startMs: number, endMs: number): { x: number; w: number } {
        const { from, to } = this._viewRange();
        const rangeDuration = to - from;
        if (rangeDuration <= 0) return { x: LABEL_WIDTH, w: 2 };

        const chartW = SVG_TOTAL_W - LABEL_WIDTH;
        const x = LABEL_WIDTH + ((startMs - from) / rangeDuration) * chartW;
        const endX = LABEL_WIDTH + ((endMs - from) / rangeDuration) * chartW;
        const w = Math.max(2, endX - x);
        return { x: clamp(x, LABEL_WIDTH, SVG_TOTAL_W), w: clamp(w, 2, SVG_TOTAL_W - x) };
    }

    // ── Event dispatch ─────────────────────────────────────────────────────────

    private _selectSpan(span: PkWaterfallSpan): void {
        this.dispatchEvent(new CustomEvent('pk-span-select', {
            bubbles:  true,
            composed: true,
            detail:   { span },
        }));
    }

    private _fireRangeZoom(fromMs: number, toMs: number): void {
        if (fromMs >= toMs) return;
        const traceStart = this._traceStartMs();
        const traceEnd   = this._traceEndMs();
        const traceDur   = traceEnd - traceStart;
        if (traceDur <= 0) return;
        const fromIso = new Date(traceStart + fromMs * traceDur).toISOString();
        const toIso   = new Date(traceStart + toMs   * traceDur).toISOString();
        this._viewFrom = traceStart + fromMs * traceDur;
        this._viewTo   = traceStart + toMs   * traceDur;
        this.dispatchEvent(new CustomEvent('pk-range-zoom', {
            bubbles:  true,
            composed: true,
            detail:   { from: fromIso, to: toIso },
        }));
        this._render();
    }

    // ── Interaction handlers ──────────────────────────────────────────────────

    private readonly _onSpanClick = (span: PkWaterfallSpan): void => {
        this._selectSpan(span);
    };

    private readonly _onSpanMouseEnter = (span: PkWaterfallSpan, e: MouseEvent): void => {
        this._tooltipSpan = span;
        this._tooltipX = e.clientX + TOOLTIP_OFFSET_X;
        this._tooltipY = e.clientY + TOOLTIP_OFFSET_Y;
        this._render();
    };

    private readonly _onSpanMouseMove = (e: MouseEvent): void => {
        if (!this._tooltipSpan) return;
        this._tooltipX = e.clientX + TOOLTIP_OFFSET_X;
        this._tooltipY = e.clientY + TOOLTIP_OFFSET_Y;
        this._render();
    };

    private readonly _onSpanMouseLeave = (): void => {
        this._tooltipSpan = null;
        this._render();
    };

    private readonly _onSpanKeyDown = (e: KeyboardEvent, span: PkWaterfallSpan, idx: number): void => {
        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                this._selectSpan(span);
                break;
            case 'ArrowDown':
                e.preventDefault();
                this._focusRow(idx + 1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                this._focusRow(idx - 1);
                break;
        }
    };

    private _focusRow(idx: number): void {
        const total = this._spans.length;
        if (total === 0) return;
        const clamped = clamp(idx, 0, total - 1);
        this._focusedIdx = clamped;
        this._render();
        requestAnimationFrame(() => {
            const el = this._root.querySelector<SVGElement>(`[data-span-idx="${clamped}"]`);
            el?.focus();
        });
    }

    // ── Minimap interaction ───────────────────────────────────────────────────

    /** Convert a clientX position to a fractional offset [0,1] on the minimap SVG. */
    private _minimapFraction(clientX: number): number {
        const svgEl = this._root.querySelector<SVGSVGElement>('.minimap-svg');
        if (!svgEl) return 0;
        const rect = svgEl.getBoundingClientRect();
        return clamp((clientX - rect.left) / rect.width, 0, 1);
    }

    private readonly _onMinimapMouseDown = (e: MouseEvent): void => {
        this._minimapDragging  = true;
        this._minimapDragStart = this._minimapFraction(e.clientX);
        this._minimapDragCur   = this._minimapDragStart;
        this._render();
    };

    private readonly _onMinimapMouseMove = (e: MouseEvent): void => {
        if (!this._minimapDragging) return;
        this._minimapDragCur = this._minimapFraction(e.clientX);
        this._render();
    };

    private readonly _onMinimapMouseUp = (e: MouseEvent): void => {
        if (!this._minimapDragging) return;
        this._minimapDragging = false;
        const end = this._minimapFraction(e.clientX);
        const from = Math.min(this._minimapDragStart, end);
        const to   = Math.max(this._minimapDragStart, end);
        this._minimapDragStart = -1;
        this._minimapDragCur   = -1;
        if (to - from > 0.01) {
            this._fireRangeZoom(from, to);
        } else {
            // reset zoom on tiny tap
            this._viewFrom = null;
            this._viewTo   = null;
            this._render();
        }
    };

    private readonly _onMinimapMouseLeave = (): void => {
        if (this._minimapDragging) {
            this._minimapDragging  = false;
            this._minimapDragStart = -1;
            this._minimapDragCur   = -1;
            this._render();
        }
    };

    // ── Minimap rendering ─────────────────────────────────────────────────────

    private _minimapTpl(): TemplateResult {
        const traceStart = this._traceStartMs();
        const traceEnd   = this._traceEndMs();
        const traceDur   = Math.max(1, traceEnd - traceStart);
        const spanH = Math.max(1, MINIMAP_H / Math.max(1, this._spans.length));
        const MM_W  = SVG_TOTAL_W;

        const lines = this._spans.map((span, i) => {
            const startFrac = (parseMs(span.start) - traceStart) / traceDur;
            const endFrac   = (parseMs(span.end)   - traceStart) / traceDur;
            const x  = clamp(startFrac * MM_W, 0, MM_W);
            const w  = Math.max(1, clamp((endFrac - startFrac) * MM_W, 1, MM_W - x));
            const y  = i * spanH;
            return svg`
                <rect
                    x=${x} y=${y}
                    width=${w} height=${Math.max(1, spanH - 0.5)}
                    fill=${span.color}
                    opacity="0.7">
                </rect>
            `;
        });

        // Viewport indicator
        const { from, to } = this._viewRange();
        const vpFrom = clamp((from - traceStart) / traceDur, 0, 1);
        const vpTo   = clamp((to   - traceStart) / traceDur, 0, 1);
        const vpX    = vpFrom * MM_W;
        const vpW    = Math.max(2, (vpTo - vpFrom) * MM_W);
        const showVp = this._viewFrom !== null && vpTo > vpFrom;

        // Drag selection overlay
        const dragOvl = this._minimapDragging && this._minimapDragStart >= 0
            ? svg`
                <rect class="minimap-select"
                    x=${Math.min(this._minimapDragStart, this._minimapDragCur) * MM_W}
                    y=${0}
                    width=${Math.abs(this._minimapDragCur - this._minimapDragStart) * MM_W}
                    height=${MINIMAP_H}>
                </rect>
              `
            : nothing;

        return html`
            <div class="minimap-wrap"
                 aria-label="Trace minimap — drag to zoom"
                 role="slider"
                 aria-valuemin="0"
                 aria-valuemax="100"
                 tabindex="0">
                <svg class="minimap-svg"
                     viewBox="0 0 ${MM_W} ${MINIMAP_H}"
                     preserveAspectRatio="none"
                     style="height: ${MINIMAP_H}px"
                     @mousedown=${this._onMinimapMouseDown}
                     @mousemove=${this._onMinimapMouseMove}
                     @mouseup=${this._onMinimapMouseUp}
                     @mouseleave=${this._onMinimapMouseLeave}>
                    ${lines}
                    ${showVp ? svg`
                        <rect class="minimap-viewport"
                              x=${vpX} y=${0}
                              width=${vpW} height=${MINIMAP_H}>
                        </rect>
                    ` : nothing}
                    ${dragOvl}
                </svg>
            </div>
        `;
    }

    // ── Ruler rendering ───────────────────────────────────────────────────────

    private _rulerTpl(): TemplateResult {
        const { from, to } = this._viewRange();
        const dur = to - from;
        if (dur <= 0) return html``;

        const TICK_COUNT = 6;
        const chartW     = SVG_TOTAL_W - LABEL_WIDTH;
        const ticks: TemplateResult[] = [];

        for (let i = 0; i <= TICK_COUNT; i++) {
            const frac = i / TICK_COUNT;
            const ms   = from + frac * dur;
            const x    = LABEL_WIDTH + frac * chartW;
            const label = fmtTime(new Date(ms).toISOString());
            ticks.push(svg`
                <line x1=${x} y1=${0} x2=${x} y2=${4}
                      stroke="var(--pk-report-axis-color, rgba(24,27,34,0.4))"
                      stroke-width="1">
                </line>
                <text class="ruler-tick-label"
                      x=${x} y=${RULER_H - 4}
                      text-anchor=${i === 0 ? 'start' : i === TICK_COUNT ? 'end' : 'middle'}>
                    ${label}
                </text>
            `);
        }

        return html`
            <div class="ruler-wrap">
                <svg class="ruler-svg"
                     viewBox="0 0 ${SVG_TOTAL_W} ${RULER_H}"
                     preserveAspectRatio="none"
                     style="height: ${RULER_H}px"
                     aria-hidden="true">
                    <line x1=${LABEL_WIDTH} y1=${0} x2=${SVG_TOTAL_W} y2=${0}
                          stroke="var(--pk-report-axis-color, rgba(24,27,34,0.4))"
                          stroke-width="1">
                    </line>
                    ${ticks}
                </svg>
            </div>
        `;
    }

    // ── Span rows rendering ───────────────────────────────────────────────────

    private _spanRowTpl(span: PkWaterfallSpan, idx: number): TemplateResult {
        const startMs = parseMs(span.start);
        const endMs   = parseMs(span.end);
        const durMs   = Math.max(0, endMs - startMs);

        const { x, w }  = this._spanToSvg(startMs, endMs);
        const y          = idx * ROW_HEIGHT;
        const barY       = y + (ROW_HEIGHT - 20) / 2;
        const BAR_HEIGHT = 20;
        const indentX    = span.depth * INDENT_PX;
        const labelX     = 4 + indentX;

        const isCritical = span.meta?.isCriticalPath === true;
        const isFocused  = this._focusedIdx === idx;

        const ariaLabel = `${span.label}, duration ${fmtDuration(durMs)}, depth ${span.depth + 1}`;

        return svg`
            <g class="span-row"
               data-span-idx="${idx}"
               role="treeitem"
               aria-level="${span.depth + 1}"
               aria-label="${ariaLabel}"
               tabindex="${idx === 0 ? '0' : '-1'}"
               @click=${() => this._onSpanClick(span)}
               @mouseenter=${(e: MouseEvent) => this._onSpanMouseEnter(span, e)}
               @mousemove=${(e: MouseEvent) => this._onSpanMouseMove(e)}
               @mouseleave=${this._onSpanMouseLeave}
               @keydown=${(e: KeyboardEvent) => this._onSpanKeyDown(e, span, idx)}>

                <!-- Row background (subtle hover target) -->
                <rect class="span-row-bg"
                      x="0" y="${y}"
                      width="${SVG_TOTAL_W}" height="${ROW_HEIGHT}"
                      fill="transparent">
                </rect>

                <!-- Alternating row shading -->
                ${idx % 2 === 1 ? svg`
                    <rect x="0" y="${y}"
                          width="${SVG_TOTAL_W}" height="${ROW_HEIGHT}"
                          fill="rgba(0,0,0,0.02)"
                          aria-hidden="true">
                    </rect>
                ` : nothing}

                <!-- Label area -->
                <text class="span-label"
                      x="${labelX}"
                      y="${y + ROW_HEIGHT / 2}"
                      clip-path="url(#label-clip)"
                      style="clip-path: inset(0 ${SVG_TOTAL_W - LABEL_WIDTH}px 0 0)">
                    ${span.label}
                </text>

                <!-- Label column right border -->
                <line x1="${LABEL_WIDTH - 1}" y1="${y}"
                      x2="${LABEL_WIDTH - 1}" y2="${y + ROW_HEIGHT}"
                      stroke="var(--pk-report-grid-color, #ECE8DA)"
                      stroke-width="0.5"
                      aria-hidden="true">
                </line>

                <!-- Span bar -->
                <rect class="span-rect ${isCritical ? 'span-rect--critical' : ''}"
                      x="${x}" y="${barY}"
                      width="${w}" height="${BAR_HEIGHT}"
                      fill="${span.color}"
                      rx="3"
                      stroke="${isCritical ? span.color : 'none'}"
                      stroke-width="${isCritical ? '1.5' : '0'}"
                      filter="${isCritical ? 'saturate(1.5)' : 'none'}">
                    <title>${span.label} — ${fmtDuration(durMs)}</title>
                </rect>

                <!-- Focus ring -->
                ${isFocused ? svg`
                    <rect class="span-focus-ring"
                          x="1" y="${y + 1}"
                          width="${SVG_TOTAL_W - 2}" height="${ROW_HEIGHT - 2}"
                          fill="none"
                          stroke="var(--pk-color-primary, #0e7c7b)"
                          stroke-width="1.5"
                          rx="2">
                    </rect>
                ` : nothing}
            </g>
        `;
    }

    // ── Tooltip ───────────────────────────────────────────────────────────────

    private _tooltipTpl(): TemplateResult {
        const span = this._tooltipSpan;
        if (!span) return html``;

        const startMs = parseMs(span.start);
        const endMs   = parseMs(span.end);
        const durMs   = Math.max(0, endMs - startMs);

        // Filter out internal fields from meta display
        const metaEntries = span.meta
            ? Object.entries(span.meta).filter(([k]) => k !== 'isCriticalPath')
            : [];

        return html`
            <div class="span-tooltip"
                 style="left: ${this._tooltipX}px; top: ${this._tooltipY}px"
                 role="tooltip"
                 aria-hidden="true">
                <div class="tooltip-label">${span.label}</div>
                <div class="tooltip-time">${fmtTime(span.start)} → ${fmtTime(span.end)}</div>
                <div class="tooltip-duration">${fmtDuration(durMs)}</div>
                ${metaEntries.length > 0 ? html`
                    <table class="tooltip-meta-table">
                        <tbody>
                            ${metaEntries.map(([k, v]) => html`
                                <tr>
                                    <td>${k}</td>
                                    <td title="${String(v)}">${
                                        typeof v === 'number' ? v.toLocaleString() : String(v)
                                    }</td>
                                </tr>
                            `)}
                        </tbody>
                    </table>
                ` : nothing}
            </div>
        `;
    }

    // ── Main template ─────────────────────────────────────────────────────────

    private _template(): TemplateResult {
        const title     = this.getAttribute('title');
        const spans     = this._spans;
        const isEmpty   = spans.length === 0;
        const showMM    = this._showMinimap();

        if (isEmpty) {
            return html`
                <style>${sharedTokens}${waterfallStyles}</style>
                <div class="waterfall-panel">
                    ${title ? html`
                        <div class="waterfall-header">
                            <p class="waterfall-title">${title}</p>
                        </div>
                    ` : nothing}
                    <div class="empty-state-wrap">
                        <p class="empty-message">No spans to display.</p>
                        <slot name="empty-state"></slot>
                    </div>
                </div>
            `;
        }

        const totalSvgH = spans.length * ROW_HEIGHT;

        return html`
            <style>${sharedTokens}${waterfallStyles}</style>
            <div class="waterfall-panel">
                ${title ? html`
                    <div class="waterfall-header">
                        <p class="waterfall-title">${title}</p>
                    </div>
                ` : nothing}

                ${showMM ? this._minimapTpl() : nothing}

                ${this._rulerTpl()}

                <div class="spans-scroll">
                    <svg class="spans-svg"
                         viewBox="0 0 ${SVG_TOTAL_W} ${totalSvgH}"
                         preserveAspectRatio="none"
                         style="height: ${totalSvgH}px; min-height: ${totalSvgH}px;"
                         role="tree"
                         aria-label="Trace waterfall — ${spans.length} spans">
                        <defs>
                            <clipPath id="label-clip">
                                <rect x="0" y="0" width="${LABEL_WIDTH - 4}" height="${totalSvgH}"></rect>
                            </clipPath>
                        </defs>
                        ${spans.map((span, i) => this._spanRowTpl(span, i))}
                    </svg>
                </div>
            </div>

            ${this._tooltipTpl()}
        `;
    }

    private _render(): void {
        render(this._template(), this._root);
    }
}

if (!customElements.get(PkReportWaterfall.tagName)) {
    customElements.define(PkReportWaterfall.tagName, PkReportWaterfall);
}

// ─────────────────────────────────────────────────────────────────────────────
// Global augmentation
// ─────────────────────────────────────────────────────────────────────────────

declare global {
    interface HTMLElementTagNameMap {
        'pk-report-waterfall': PkReportWaterfall;
    }
}
